import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { difference, sortBy } from 'es-toolkit';
import { Stack, DialogTitle, Typography, TextField, Button, Autocomplete, Divider, List } from '@mui/material';

import { allGroupSensorsAtom, defaultGroupIdAtom, groupSensorsAtom, selectedGroupIdAtom } from '@/interfaces/group';
import { patchDeviceGroupAtom } from '@/interfaces/devices';
import ListItemWithDelete from '@/components/common/ListItemWithDelete';

import type { SyntheticEvent } from 'react';
import type { AutocompleteChangeReason } from '@mui/material';

interface ManageDevicesDialogProps {
  groupName: string;
  onClose: () => void;
}
interface AutocompleteItem {
  label: string;
  id: string;
}

export default function ManageDevicesDialog({ groupName, onClose }: ManageDevicesDialogProps) {
  const { data: allSensors } = useAtomValue(allGroupSensorsAtom);
  const { data: sensors } = useAtomValue(groupSensorsAtom);
  const { data: isSuccess, mutate, isPending } = useAtomValue(patchDeviceGroupAtom);
  const groupId = useAtomValue(selectedGroupIdAtom);
  const defaultGroupId = useAtomValue(defaultGroupIdAtom);

  const [autocompleteList, setAutocompleteList] = useState<AutocompleteItem[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<AutocompleteItem>(null);
  const [inputValue, setInputValue] = useState('');
  const [selectedDevices, setSelectedDevices] = useState<AutocompleteItem[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (allSensors === undefined || sensors === undefined) return;

    const selectedSensorIds = sensors.map((sensor) => sensor.id);
    const selectedItems: AutocompleteItem[] = [];
    const autocompleteItems: AutocompleteItem[] = [];
    for (const sensor of allSensors) {
      if (selectedSensorIds.includes(sensor.id)) {
        selectedItems.push({ label: sensor.name, id: sensor.id });
      } else {
        autocompleteItems.push({ label: sensor.name, id: sensor.id });
      }
    }
    setSelectedDevices(sortBy(selectedItems, ['label']));
    setAutocompleteList(sortBy(autocompleteItems, ['label']));
  }, [allSensors, sensors]);
  useEffect(() => {
    if (isSuccess === undefined) return;
    if (isSuccess) {
      onClose();
    } else {
      window.alert(t('Settings.message.editFailed'));
    }
  }, [isSuccess, onClose, t]);

  function onAutocompleteChange(_: SyntheticEvent, item: AutocompleteItem, reason: AutocompleteChangeReason) {
    if (reason !== 'selectOption') return;
    setAutocompleteList(autocompleteList.filter((device) => device.id !== item.id));
    setSelectedDevices([...selectedDevices, item]);
    setSelectedDevice(null);
    setInputValue('');
  }
  function onRemoveClick(item: AutocompleteItem) {
    setSelectedDevices(selectedDevices.filter((device) => device.id !== item.id));
    setAutocompleteList(sortBy([...autocompleteList, item], ['label']));
  }
  function onSaveClick() {
    const oldGroupSensorIds = sensors.map((sensor) => sensor.id);
    const newGroupSensorIds = selectedDevices.map((device) => device.id);
    const sensorIdsToAdd = difference(newGroupSensorIds, oldGroupSensorIds);
    const sensorIdsToRemove = difference(oldGroupSensorIds, newGroupSensorIds);

    for (const sensorId of sensorIdsToAdd) {
      mutate({ groupId, sensorId });
    }
    for (const sensorId of sensorIdsToRemove) {
      mutate({ groupId: defaultGroupId, sensorId });
    }
  }

  return (
    <Stack gap={2} sx={{ minWidth: 480, p: 3 }}>
      <DialogTitle sx={{ px: 0 }}>{t('Common.label.manageDevices')}</DialogTitle>
      <Typography>{t('Settings.label.groupName')}</Typography>
      <TextField disabled value={groupName} />
      <Typography>{t('Settings.label.selectDevice')}</Typography>
      <Autocomplete
        autoHighlight
        options={autocompleteList}
        value={selectedDevice}
        inputValue={inputValue}
        onChange={onAutocompleteChange}
        onInputChange={(_, value) => setInputValue(value)}
        renderInput={(params) => <TextField {...params} />}
      />
      <List>
        <Divider />
        {selectedDevices.map((device) => (
          <ListItemWithDelete key={device.id} label={device.label} onRemoveClick={() => onRemoveClick(device)} />
        ))}
      </List>

      <Stack direction="row" justifyContent="center" gap={3}>
        <Button variant="contained" onClick={onClose} sx={{ bgcolor: '#CCC', color: 'black' }}>
          {t('Common.label.cancel')}
        </Button>
        <Button variant="contained" disabled={isPending} onClick={onSaveClick} sx={{ bgcolor: 'info.main' }}>
          {t('Common.label.saveButton')}
        </Button>
      </Stack>
    </Stack>
  );
}
